import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../components/Common/TableContainer";
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";

const MalwareHistory = () => {
  document.title = "History";

  const [taskData, setTaskData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;

  // Empty state component
  const EmptyState = () => (
    <div className="text-center p-5">
      <div className="mb-4">
        <i className="mdi mdi-file-search text-primary" style={{ fontSize: "4rem" }} />
      </div>
      <h3 className="mb-3 font-weight-bold">No Malware Scans Found</h3>
      <p className="text-muted mb-4">
        You haven't performed any malware analysis scans yet. Start by analyzing a file or URL to detect potential threats.
      </p>
      <div className="d-flex justify-content-center">
        <Link to="/malwareAnalysis" className="btn btn-primary px-4 py-2">
          Start New Analysis
        </Link>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://api.bluhawk.live/malware-scan/${userId}`);
        const data = await response.json();
        setTaskData(data.reverse());
      } catch (error) {
        console.error("Error fetching task data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTaskData();
  }, [userId]);

  const onClickData = (task) => {
    setSelectedTask(task);
    setDeleteModal(true);
  };

  const handleDeleteTask = async (scanId) => {
    try {
      // Prepare the request body with "Malware Scan" as the category
      const requestBody = {
        scanId: scanId,  // Use scanId
        category: "Malware Scan",  // Set category to "Malware Scan"
      };
      console.log("Request body:", requestBody);
      // Send the DELETE request to the API with the JSON body
      const response = await fetch(`https://api.bluhawk.live/trash/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json', // Set the content type
        },
        body: JSON.stringify(requestBody), // Send the JSON payload
      });
      console.log("Response:", response.text());
      if (response.ok) {
        // If deletion is successful, remove the task from the list in state
        setTaskData(prevTasks => prevTasks.filter(task => task.scanId !== scanId));
        console.log('Task deleted successfully');
      } else {
        console.error('Error deleting task:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };
  
  const onClickDelete = (task) => {
    // Directly call the delete API when the delete button is clicked
    handleDeleteTask(task.scanId); // Pass only the scanId, category is fixed as "Malware Scan"
  };
  
  
 

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "scanId",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => (
          <Link to="#" className="text-body fw-bold">
            {cellProps.row.original.scanId}
          </Link>
        ),
      },
      {
        header: "Asset Name",
        accessorKey: "assetname",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Initiated",
        accessorKey: "timestamp",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Last Scan",
        accessorKey: "timestamp",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          const status = cellProps.row.original.status.toLowerCase();
          const badgeClass = status === "in progress" 
            ? "bg-info" 
            : status === "completed" 
              ? "bg-success" 
              : "bg-secondary";
          return <Badge className={badgeClass}>{cellProps.row.original.status}</Badge>;
        },
      },
      {
        header: "Resolved",
        accessorKey: "resolved",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const { resolved, scanId } = cellProps.row.original;
      
          // If resolved is null or undefined, show placeholder "-"
          if (resolved === null || resolved === undefined) {
            return <span>-</span>;
          }
      
          // Function to toggle resolved status
          const toggleResolvedStatus = async () => {
            const newResolvedStatus = !resolved;
      
            // Optimistically update the state: Update the task data in the state
            setTaskData((prevTaskData) =>
              prevTaskData.map((task) =>
                task.scanId === scanId ? { ...task, resolved: newResolvedStatus } : task
              )
            );
      
            try {
              // Send the backend update request
              const response = await fetch(`https://api.bluhawk.live/malware-scan/${userId}/results/${scanId}`, {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ scanId, resolved: newResolvedStatus }),
              });
      
              if (!response.ok) {
                // If the update fails, revert the optimistic UI update
                console.error("Failed to update resolved status:", response.statusText);
      
                // Revert state to its previous value in case of failure
                setTaskData((prevTaskData) =>
                  prevTaskData.map((task) =>
                    task.scanId === scanId ? { ...task, resolved } : task
                  )
                );
              }
            } catch (error) {
              // In case of an error, revert the state as well
              console.error("Error updating resolved status:", error);
      
              // Revert state in case of error
              setTaskData((prevTaskData) =>
                prevTaskData.map((task) =>
                  task.scanId === scanId ? { ...task, resolved } : task
                )
              );
            }
          };
      
          return (
            <Button
              color={resolved ? "success" : "danger"}
              size="sm"
              onClick={toggleResolvedStatus}
              disabled={resolved === null || resolved === undefined}
            >
              {resolved ? "Resolved" : "Unresolved"}
            </Button>
          );
        },
      },
      
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          const task = cellProps.row.original;
          return (
            <div className="list-unstyled hstack gap-1 mb-0">
              <li>
                <Link
                  to={`/malware/${task.scanId}`}
                  className="btn btn-sm btn-soft-primary"
                  id={`viewtooltip${task.scanId}`}
                >
                  <i className="mdi mdi-eye-outline" />
                  <UncontrolledTooltip placement="top" target={`viewtooltip${task.scanId}`}>
                    View
                  </UncontrolledTooltip>
                </Link>
              </li>
              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => onClickDelete(task)} // Pass scanId correctly
                  id={`deletetooltip${task.scanId}`}
                >
                  <i className="mdi mdi-delete-outline" />
                  <UncontrolledTooltip placement="top" target={`deletetooltip${task.scanId}`}>
                    Delete
                  </UncontrolledTooltip>
                </Link>
              </li>
            </div>
          );
        },
      },
    ],
    []
  );
  
  

  return (
    <React.Fragment>
     
      
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Behaviour Analysis" breadcrumbItem="History" />
          
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {isLoading ? (
                    <div className="text-center p-4">
                      <Spinners setLoading={setLoading} />
                    </div>
                  ) : taskData.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={taskData}
                      isGlobalFilter={true}
                      isJobListGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search tasks..."
                      tableClass="table-bordered align-middle nowrap mt-2"
                      pagination="pagination pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withRouter(MalwareHistory);